import { Connection, PublicKey, SystemProgram, Transaction, TransactionInstruction, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { Program, AnchorProvider, BN } from '@coral-xyz/anchor';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import ZnoospaceIDL from '../idl/chirpx_solana.json';
import { secureStorage } from '../utils/security';
import { formatAddressAsUsername, formatAddressForDisplay, generateUniqueDisplayName, validateDisplayName, isValidSolanaAddress } from '../utils/addressUtils';

// Znoospace Program ID (deployed on devnet)
export const ZNOOSPACE_PROGRAM_ID = new PublicKey('6eYBoAEeB5VXEfSGmkejDfTmmBxeqDyV5QWr19eF9Zst');

// Contract creator wallet address (receives 0.01% fee)
const CONTRACT_CREATOR_WALLET = new PublicKey('9r3aFfNuu8pqG3jEkjDNhpm6BW7p87Qe2XHKaCUkRCip'); // Replace with actual creator wallet

// Post Types
export const PostType = {
  Text: { text: {} },
  Image: { image: {} },
  Video: { video: {} },
  Poll: { poll: {} }
};

// Solana Service Class
export class SolanaService {
  constructor(connection, wallet) {
    // SolanaService constructor initialized
    
    this.connection = connection;
    this.wallet = wallet;
    this.program = null;
    this.programId = '6eYBoAEeB5VXEfSGmkejDfTmmBxeqDyV5QWr19eF9Zst';
    this.fallbackMode = false;
    
    // Initialize fallback data structures
    this.fallbackPosts = [];
    this.userProfiles = new Map();
    
    // Scaling features
    this.beamCache = new Map(); // Cache for beams
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.pageSize = 20; // Default page size
    this.maxCacheSize = 1000; // Max cached beams
    
    if (wallet && wallet.publicKey) {
      try {
        // Initializing Anchor program
        
        // Validate wallet adapter methods
        if (!wallet.signTransaction || !wallet.signAllTransactions) {
          throw new Error('Wallet missing required signing methods');
        }
        
        // Create a proper wallet adapter for Anchor
        const walletAdapter = {
          publicKey: wallet.publicKey,
          signTransaction: async (tx) => {
            // Signing transaction
            return await wallet.signTransaction(tx);
          },
          signAllTransactions: async (txs) => {
            // Signing all transactions
            return await wallet.signAllTransactions(txs);
          },
        };
        
        // Creating AnchorProvider
        this.provider = new AnchorProvider(
          connection, 
          walletAdapter, 
          { 
            commitment: 'confirmed',
            preflightCommitment: 'confirmed'
          }
        );
        
        // Creating Anchor Program
        
        try {
          // Initializing smart contract connection
          
          // Create the Anchor program to interact with smart contract
          this.program = new Program(ZnoospaceIDL, ZNOOSPACE_PROGRAM_ID, this.provider);
          
          if (this.program && this.program.methods) {
            // Smart contract connection established
            
            // Test the program by checking if we can access the methods
            const requiredMethods = ['createUserProfile', 'createPost', 'likePost'];
            const availableMethods = Object.keys(this.program.methods);
            
            for (const method of requiredMethods) {
              if (!availableMethods.includes(method)) {
                throw new Error(`Required method '${method}' not found in smart contract`);
              }
            }
            
            // Initialize blockchain mode - all operations go through smart contract
            this.usingDirectMode = false;
            // All operations will call smart contract on Solana devnet
            
            // Initialize user profiles cache for display name checking
            this.userProfiles = new Map();
            
            // Initialize platform if needed (async operation)
            this.initializePlatform();
            
          } else {
            throw new Error('Smart contract methods not available');
          }
          
        } catch (contractError) {
          console.error('❌ Smart contract connection failed:', contractError);
          console.error('❌ Error details:', contractError.message);
          console.warn('⚠️ Falling back to local storage mode for profile management');
          
          // Initialize fallback mode instead of throwing error
          this.program = null;
          this.usingDirectMode = true;
          this.userProfiles = new Map();
          
          // Running in fallback mode - profiles will use localStorage
        }
        
      } catch (error) {
        console.error('❌ CRITICAL: Anchor program initialization failed:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack
        });
        
        // Initialize fallback mode
        this.program = null;
        this.usingDirectMode = true;
        this.userProfiles = new Map();
        this.fallbackPosts = [];
        
        // SolanaService running in fallback mode
      }
    } else {
      // Wallet or publicKey not available, skipping program initialization
    }
  }

  // Create a compatible IDL structure for Anchor
  createCompatibleIDL(originalIDL) {
    return {
      version: "0.1.0",
      name: "znoospace_solana",
      instructions: [
        {
          name: "createUserProfile",
          accounts: [
            { name: "userProfile", isMut: true, isSigner: false },
            { name: "platform", isMut: true, isSigner: false },
            { name: "authority", isMut: true, isSigner: true },
            { name: "systemProgram", isMut: false, isSigner: false }
          ],
          args: [
            { name: "username", type: "string" },
            { name: "displayName", type: "string" },
            { name: "bio", type: "string" },
            { name: "avatarUrl", type: "string" }
          ]
        },
        {
          name: "createPost",
          accounts: [
            { name: "post", isMut: true, isSigner: false },
            { name: "userProfile", isMut: true, isSigner: false },
            { name: "authority", isMut: true, isSigner: true },
            { name: "systemProgram", isMut: false, isSigner: false }
          ],
          args: [
            { name: "content", type: "string" },
            { name: "imageUrl", type: "string" }
          ]
        },
        {
          name: "likePost",
          accounts: [
            { name: "like", isMut: true, isSigner: false },
            { name: "post", isMut: true, isSigner: false },
            { name: "authority", isMut: true, isSigner: true },
            { name: "systemProgram", isMut: false, isSigner: false }
          ],
          args: []
        }
      ],
      accounts: [
        {
          name: "UserProfile",
          type: {
            kind: "struct",
            fields: [
              { name: "authority", type: "publicKey" },
              { name: "username", type: "string" },
              { name: "displayName", type: "string" },
              { name: "bio", type: "string" },
              { name: "avatarUrl", type: "string" },
              { name: "postsCount", type: "u64" },
              { name: "followersCount", type: "u64" },
              { name: "followingCount", type: "u64" },
              { name: "createdAt", type: "i64" }
            ]
          }
        },
        {
          name: "Post",
          type: {
            kind: "struct",
            fields: [
              { name: "authority", type: "publicKey" },
              { name: "content", type: "string" },
              { name: "imageUrl", type: "string" },
              { name: "likesCount", type: "u64" },
              { name: "repostsCount", type: "u64" },
              { name: "createdAt", type: "i64" }
            ]
          }
        },
        {
          name: "Like",
          type: {
            kind: "struct",
            fields: [
              { name: "authority", type: "publicKey" },
              { name: "post", type: "publicKey" },
              { name: "createdAt", type: "i64" }
            ]
          }
        }
      ]
    };
  }

  // Create a minimal IDL structure as fallback
  createMinimalIDL() {
    return {
      version: "0.1.0",
      name: "znoospace_solana",
      instructions: [
        {
          name: "createUserProfile",
          accounts: [
            { name: "userProfile", isMut: true, isSigner: false },
            { name: "authority", isMut: true, isSigner: true },
            { name: "systemProgram", isMut: false, isSigner: false }
          ],
          args: [
            { name: "username", type: "string" },
            { name: "displayName", type: "string" }
          ]
        }
      ],
      accounts: [
        {
          name: "UserProfile",
          type: {
            kind: "struct",
            fields: [
              { name: "authority", type: "publicKey" },
              { name: "username", type: "string" },
              { name: "displayName", type: "string" }
            ]
          }
        }
      ]
    };
  }

  // Get Platform PDA
  async getPlatformPDA() {
    const [platformPDA] = await PublicKey.findProgramAddress(
      [Buffer.from('platform')],
      ZNOOSPACE_PROGRAM_ID
    );
    return platformPDA;
  }

  // Get User Profile PDA
  async getUserProfilePDA(userPublicKey) {
    const [userProfilePDA] = await PublicKey.findProgramAddress(
      [Buffer.from('user_profile'), userPublicKey.toBuffer()],
      ZNOOSPACE_PROGRAM_ID
    );
    return userProfilePDA;
  }

  // Get Post PDA
  async getPostPDA(userPublicKey, postCount) {
    const [postPDA] = await PublicKey.findProgramAddress(
      [Buffer.from('post'), userPublicKey.toBuffer(), new BN(postCount).toArrayLike(Buffer, 'le', 8)],
      ZNOOSPACE_PROGRAM_ID
    );
    return postPDA;
  }

  // Get Like PDA
  async getLikePDA(userPublicKey, postPublicKey) {
    const [likePDA] = await PublicKey.findProgramAddress(
      [Buffer.from('like'), userPublicKey.toBuffer(), postPublicKey.toBuffer()],
      ZNOOSPACE_PROGRAM_ID
    );
    return likePDA;
  }

  // Get Follow PDA
  async getFollowPDA(followerPublicKey, followingPublicKey) {
    const [followPDA] = await PublicKey.findProgramAddress(
      [Buffer.from('follow'), followerPublicKey.toBuffer(), followingPublicKey.toBuffer()],
      ZNOOSPACE_PROGRAM_ID
    );
    return followPDA;
  }

  // Initialize platform if needed
  async initializePlatform() {
    try {
      // Checking if platform needs initialization
      const [platformPDA] = await this.getPlatformPDA();
      
      // Try to fetch platform account
      try {
        const platformAccount = await this.program.account.platform.fetch(platformPDA);
        // Platform already initialized
        return platformAccount;
      } catch (fetchError) {
        // Platform doesn't exist, need to initialize it
        // Platform not found, initializing
        
        const tx = await this.program.methods
          .initializePlatform()
          .accounts({
            platform: platformPDA,
            authority: this.wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        
        // Platform initialized successfully
        
        return await this.program.account.platform.fetch(platformPDA);
      }
    } catch (error) {
      console.error('❌ Platform initialization failed:', error);
      // Don't throw error - platform initialization is optional for user operations
    }
  }

  // Check if user profile exists on blockchain
  async checkUserProfileExists(userPublicKey) {
    try {
      if (!this.program) {
        // Anchor program not available, checking fallback storage
        return this.checkUserProfileFallback(userPublicKey);
      }
      
      const [userProfilePDA] = await this.getUserProfilePDA(userPublicKey);
      const userProfile = await this.program.account.userProfile.fetch(userProfilePDA);
      
      console.log('✅ User profile found on blockchain:', {
        username: userProfile.username,
        displayName: userProfile.displayName,
        authority: userProfile.authority.toString()
      });
      
      return {
        exists: true,
        profile: {
          username: userProfile.username,
          displayName: userProfile.displayName,
          bio: userProfile.bio,
          avatarUrl: userProfile.avatarUrl,
          authority: userProfile.authority.toString(),
          postsCount: userProfile.postsCount.toNumber(),
          followersCount: userProfile.followersCount.toNumber(),
          followingCount: userProfile.followingCount.toNumber(),
          createdAt: userProfile.createdAt.toNumber(),
          isVerified: userProfile.isVerified
        },
        pda: userProfilePDA
      };
    } catch (error) {
      // User profile not found on blockchain, checking fallback
      return this.checkUserProfileFallback(userPublicKey);
    }
  }

  // Check user profile from fallback storage
  checkUserProfileFallback(userPublicKey) {
    try {
      const walletAddress = userPublicKey.toString();
      // Checking fallback storage for profile
      
      // Check localStorage for existing profile
      const rawProfiles = secureStorage.getItem('znoospace_profiles');
      const profiles = rawProfiles ? (typeof rawProfiles === 'string' ? JSON.parse(rawProfiles) : rawProfiles) : {};
      const profile = profiles[walletAddress];
      
      if (profile && profile.status === 'completed') {
        // Profile found in fallback storage
        
        return {
          exists: true,
          profile: {
            username: profile.username,
            displayName: profile.displayName,
            bio: profile.bio || '',
            avatarUrl: profile.avatarUrl || '',
            authority: walletAddress,
            postsCount: 0,
            followersCount: 0,
            followingCount: 0,
            createdAt: Date.now(),
            isVerified: false
          }
        };
      }
      
      // No profile found in fallback storage
      return {
        exists: false,
        profile: null
      };
    } catch (error) {
      console.error('❌ Error checking fallback storage:', error);
      return {
        exists: false,
        profile: null
      };
    }
  }

  // Create user profile with address-based username and display name uniqueness
  async createUserProfile(displayName, bio = '', avatarUrl = '') {
    try {
      // Defensive: ensure displayName is always a plain string
      if (displayName && typeof displayName === 'object') {
        displayName = displayName.displayName || displayName.name || displayName.username || '';
      }
      displayName = String(displayName || '').trim();
      
      if (!this.wallet || !this.wallet.publicKey) {
        throw new Error('Wallet not connected');
      }
      
      const userAddress = this.wallet.publicKey.toString();
      
      // Generate address-based username (always use address format)
      const username = formatAddressAsUsername(userAddress);
      
      // Validate display name
      const validation = validateDisplayName(displayName);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }
      
      // Check if display name is already taken
      let finalDisplayName = displayName;
      const isDisplayNameTaken = await this.checkDisplayNameExists(displayName);
      
      if (isDisplayNameTaken) {
        // Generate unique display name using address prefix
        finalDisplayName = generateUniqueDisplayName(displayName, userAddress);
        // Display name taken, suggesting alternative
        
        // Ask user if they want to use the suggested name
        const userAccepts = window.confirm(
          `The display name '${displayName}' is already taken.\n\n` +
          `Would you like to use '${finalDisplayName}' instead?\n\n` +
          `Click OK to use the suggested name, or Cancel to choose a different one.`
        );
        
        if (!userAccepts) {
          throw new Error('Display name is already taken. Please choose a different name.');
        }
      }
      
      // Creating user profile on blockchain using memo transaction
      
      // Use admin panel approach: direct memo transaction instead of Anchor
      const { Connection, Transaction, TransactionInstruction, PublicKey } = await import('@solana/web3.js');
      
      // Create connection
      const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
      
      // Generate highly unique nonce with multiple entropy sources
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 15);
      const microTime = performance.now().toString().replace('.', '');
      const uniqueNonce = `${timestamp}_${randomSuffix}_${microTime}_${Math.floor(Math.random() * 1000000)}`;
      
      // Get fresh blockhash FIRST for maximum uniqueness
      const { blockhash } = await connection.getLatestBlockhash('finalized');
      
      // Add random delay to ensure transaction uniqueness
      const randomDelay = Math.floor(Math.random() * 200) + 100; // 100-300ms
      await new Promise(resolve => setTimeout(resolve, randomDelay));
      
      const blockchainProfileData = {
        displayName,
        username: username || `${userAddress.slice(0, 3)}...${userAddress.slice(-4)}`,
        bio: bio || '',
        avatarUrl: avatarUrl || '',
        author: userAddress,
        timestamp: new Date().toISOString(),
        nonce: uniqueNonce,
        profileId: `profile_${timestamp}_${randomSuffix}_${microTime}`,
        blockhash: blockhash.slice(0, 8), // Include part of blockhash for uniqueness
        version: '1.1'
      };
      
      // Profile data for blockchain prepared
      
      // Create transaction with memo instruction using enhanced uniqueness
      const transaction = new Transaction();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = new PublicKey(userAddress);
      
      // Create memo data with enhanced uniqueness markers
      const memoData = `Znoospace Profile Creation [${uniqueNonce}]: ${JSON.stringify(blockchainProfileData)}`;
      const memoInstruction = new TransactionInstruction({
        keys: [{
          pubkey: new PublicKey(userAddress),
          isSigner: true,
          isWritable: false
        }],
        programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'), // Memo program
        data: Buffer.from(memoData, 'utf8')
      });
      
      transaction.add(memoInstruction);
      
      // Transaction prepared for signing
      
      // Requesting wallet signature for profile creation
      
      // Sign and send transaction using wallet
      let signedTransaction;
      if (window.solana && window.solana.isPhantom) {
        signedTransaction = await window.solana.signTransaction(transaction);
      } else {
        throw new Error('Phantom wallet not available for signing');
      }
      
      // Sending profile creation transaction to Solana
      
      // Send the signed transaction
      const tx = await connection.sendRawTransaction(signedTransaction.serialize());
      
      // Confirming transaction
      
      // Confirm the transaction
      await connection.confirmTransaction(tx, 'confirmed');
      
      // Profile created successfully on blockchain
      
      // Cache the profile for display name uniqueness checking
      this.userProfiles.set(userAddress, {
        username,
        displayName: finalDisplayName,
        bio,
        avatarUrl,
        authority: userAddress,
        createdAt: Date.now()
      });
      
      return {
        success: true,
        signature: tx,
        profile: {
          username,
          displayName: finalDisplayName,
          bio,
          avatarUrl,
          authority: userAddress
        },
        explorerUrl: `https://explorer.solana.com/tx/${tx}?cluster=devnet`
      };
      
    } catch (error) {
      console.error('❌ Error creating user profile on blockchain:', error);
      throw new Error(`Failed to create user profile: ${error.message}`);
    }
  }

  // Check if display name already exists
  async checkDisplayNameExists(displayName) {
    try {
      // For now, we'll use a simple check against our cache
      // In a full implementation, this would query the blockchain
      for (const [address, profile] of this.userProfiles) {
        if (profile.displayName.toLowerCase() === displayName.toLowerCase()) {
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking display name:', error);
      return false; // Default to allowing the name if check fails
    }
  }

  // Create a new post
  async createPost(content, mediaUrls = [], postType = PostType.Text) {
    if (!this.wallet || !this.wallet.publicKey) {
      throw new Error('Wallet not connected');
    }
    
    if (!this.program) {
      // Anchor program not available, using direct Solana transaction for post creation
      return this.createPostTransaction(content, mediaUrls[0] || '');
    }

    try {
      const userProfilePDA = await this.getUserProfilePDA(this.wallet.publicKey);
      const platformPDA = await this.getPlatformPDA();
      
      // Get user profile to determine post count
      const userProfile = await this.program.account.userProfile.fetch(userProfilePDA);
      const postPDA = await this.getPostPDA(this.wallet.publicKey, userProfile.postsCount);

      const tx = await this.program.methods
        .createPost(content, mediaUrls, postType)
        .accounts({
          post: postPDA,
          userProfile: userProfilePDA,
          platform: platformPDA,
          authority: this.wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      return { 
        success: true, 
        signature: tx, 
        postPDA,
        explorerUrl: `https://explorer.solana.com/tx/${tx}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  // Like a post
  async likePost(postPublicKey) {
    if (!this.program || !this.wallet.publicKey) {
      throw new Error('Wallet not connected');
    }

    try {
      const likePDA = await this.getLikePDA(this.wallet.publicKey, postPublicKey);

      const tx = await this.program.methods
        .likePost()
        .accounts({
          like: likePDA,
          post: postPublicKey,
          authority: this.wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      return { 
        success: true, 
        signature: tx,
        explorerUrl: `https://explorer.solana.com/tx/${tx}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error liking post:', error);
      throw error;
    }
  }

  // Follow another user
  async followUser(targetUserPublicKey) {
    if (!this.program || !this.wallet.publicKey) {
      throw new Error('Wallet not connected');
    }

    try {
      const followerProfilePDA = await this.getUserProfilePDA(this.wallet.publicKey);
      const followingProfilePDA = await this.getUserProfilePDA(targetUserPublicKey);
      const followPDA = await this.getFollowPDA(this.wallet.publicKey, targetUserPublicKey);

      const tx = await this.program.methods
        .followUser()
        .accounts({
          follow: followPDA,
          followerProfile: followerProfilePDA,
          followingProfile: followingProfilePDA,
          authority: this.wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      return { 
        success: true, 
        signature: tx,
        explorerUrl: `https://explorer.solana.com/tx/${tx}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error following user:', error);
      throw error;
    }
  }

  // Get user profile data
  async getUserProfile(userPublicKey) {
    if (!this.program) {
      throw new Error('Program not initialized - blockchain connectivity required');
    }

    try {
      // Fetching user profile from blockchain
      const result = await this.checkUserProfileExists(userPublicKey);
      
      if (result.exists) {
        // User profile retrieved from blockchain
        return { 
          success: true, 
          data: result.profile, 
          pda: result.pda,
          exists: true
        };
      } else {
        // User profile does not exist on blockchain
        return { 
          success: false, 
          error: 'User profile not found on blockchain',
          exists: false
        };
      }
    } catch (error) {
      console.error('❌ Error fetching user profile from blockchain:', error);
      return { 
        success: false, 
        error: error.message,
        exists: false
      };
    }
  }

  // Get post account data
  async getPostAccount(postPDA) {
    if (!this.program) {
      throw new Error('Program not initialized');
    }

    try {
      const postAccount = await this.program.account.post.fetch(postPDA);
      return { success: true, data: postAccount };
    } catch (error) {
      console.error('Error fetching post account:', error);
      return { success: false, error: error.message };
    }
  }

  // Get all posts for a user
  async getUserPosts(userPublicKey) {
    if (!this.program) {
      throw new Error('Program not initialized');
    }

    try {
      const posts = await this.program.account.post.all([
        {
          memcmp: {
            offset: 8, // Skip discriminator
            bytes: userPublicKey.toBase58(),
          },
        },
      ]);

      return { success: true, data: posts };
    } catch (error) {
      console.error('Error fetching user posts:', error);
      return { success: false, error: error.message };
    }
  }

  // @DEPRECATED: Use useZnoospaceProgram hook's getBeams() instead.
  // This method uses raw RPC N+1 calls and does NOT use Helius.
  // Kept only for backward compatibility with DiagnosticTest and legacy components.
  async getBeams(page = 1, limit = null) {
    console.warn('⚠️ DEPRECATED: solanaService.getBeams() called. Use useZnoospaceProgram hook instead for Helius-optimized loading.');
    try {
      const pageSize = limit || this.pageSize;
      const cacheKey = `beams_page_${page}_${pageSize}`;
      
      // Check cache first
      const cached = this.getCachedBeams(cacheKey);
      if (cached) {
        // Returning cached beams for page
        return cached;
      }

      // Loading beams page
      
      // Try to load beams from blockchain first
      let allBeams = [];
      try {
        // Querying blockchain for Znoospace beams
        allBeams = await this.queryZnoospaceBeamsFromBlockchain();
        // Found beams from blockchain
      } catch (blockchainError) {
        // Blockchain query failed, using fallback
      }
      
      // If no blockchain beams found, try smart contract (if available)
      if (allBeams.length === 0 && this.program) {
        try {
          const contractBeams = await this.program.account.post.all();
          allBeams = contractBeams.map(beam => ({
            id: beam.publicKey.toString(),
            content: beam.account.content,
            author: beam.account.author.toString(),
            username: beam.account.username || 'Unknown',
            displayName: beam.account.displayName || 'Unknown User',
            timestamp: new Date(beam.account.timestamp * 1000).toISOString(),
            likes: beam.account.likes || 0,
            mediaUrl: beam.account.mediaUrl || ''
          }));
        } catch (contractError) {
          // Smart contract query failed
        }
      }
      
      // If still no beams, use fallback data
      if (allBeams.length === 0) {
        // No blockchain beams found, using fallback beams
        return this.getPaginatedFallbackBeams(page, pageSize);
      }
      
      // Sort beams by timestamp (newest first)
      const sortedBeams = allBeams.sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt));
      
      // Paginate results
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedBeams = sortedBeams.slice(startIndex, endIndex);
      
      // Cache the results
      const result = {
        beams: paginatedBeams,
        page,
        pageSize,
        totalBeams: sortedBeams.length,
        hasMore: endIndex < sortedBeams.length
      };
      
      this.cacheBeams(cacheKey, result);
      
      return result;
      
    } catch (error) {
      console.error('Error fetching beams:', error);
      const pageSize = limit || this.pageSize;
      return this.getPaginatedFallbackBeams(page, pageSize);
    }
  }

  // Legacy method for backward compatibility
  async getAllPosts() {
    const result = await this.getBeams(1, 100); // Get first 100 beams
    return result.beams || [];
  }

  // Create beam method (alias for createPost for UI compatibility)
  async createBeam(content, mediaUrls = []) {
    // Creating beam
    return await this.createPost(content, mediaUrls);
  }

  // Fallback method for getting all posts
  async getAllPostsFallback() {
    // Using fallback to get all posts
    
    // Return posts in the format expected by the UI
    const formattedPosts = this.fallbackPosts.map(post => ({
      account: {
        author: {
          toString: () => post.authority
        },
        content: post.content,
        imageUrl: post.imageUrl || '',
        likesCount: post.likesCount || 0,
        repostsCount: post.repostsCount || 0,
        createdAt: { toNumber: () => post.createdAt }
      },
      publicKey: {
        toString: () => post.id
      }
    }));
    
    return { success: true, data: formattedPosts };
  }

  // Check if user has liked a post
  async hasLikedPost(userPublicKey, postPublicKey) {
    if (!this.program) {
      throw new Error('Program not initialized');
    }

    try {
      const likePDA = await this.getLikePDA(userPublicKey, postPublicKey);
      await this.program.account.like.fetch(likePDA);
      return { success: true, hasLiked: true };
    } catch (error) {
      // If account doesn't exist, user hasn't liked the post
      return { success: true, hasLiked: false };
    }
  }

  // Check if user is following another user
  async isFollowing(followerPublicKey, followingPublicKey) {
    if (!this.program) {
      throw new Error('Program not initialized');
    }

    try {
      const followPDA = await this.getFollowPDA(followerPublicKey, followingPublicKey);
      await this.program.account.follow.fetch(followPDA);
      return { success: true, isFollowing: true };
    } catch (error) {
      // If account doesn't exist, user is not following
      return { success: true, isFollowing: false };
    }
  }

  // @DEPRECATED: Use useZnoospaceProgram hook's getBeams() or Helius API instead.
  // This method scans the global Memo program (99%+ noise) with N+1 RPC calls.
  async queryZnoospaceBeamsFromBlockchain(walletAddress = null) {
    console.warn('⚠️ DEPRECATED: solanaService.queryZnoospaceBeamsFromBlockchain() called. Use Helius API instead.');
    try {
      // Querying blockchain for Znoospace beams
      
      // GLOBAL TIMELINE: Query memo program for ALL users' posts (social feed)
    // If we have a specific wallet address, query that wallet's transactions directly
    // Otherwise, query the memo program for all transactions (global timeline)
    let targetAddress;
    if (walletAddress) {
      targetAddress = new PublicKey(walletAddress);
      // Querying specific wallet
    } else {
      // GLOBAL TIMELINE: Always query memo program for all transactions
      targetAddress = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');
      // Querying memo program for ALL users' transactions (global timeline)
    }  
      
      let allSignatures = [];
      let beforeSignature = null;
      
      // OPTIMIZED: Reduce RPC calls to prevent rate limiting
    // Load fewer pages initially to avoid 429 errors
    const maxPages = 2; // Load up to 200 transactions (100 per page) - reduced from 5
      
      for (let page = 0; page < maxPages; page++) {
        try {
          const signatures = await this.connection.getSignaturesForAddress(
            targetAddress,
            {
              limit: 100, // Maximum per request
              before: beforeSignature
            }
          );
          
          if (signatures.length === 0) break; // No more transactions
          
          allSignatures = allSignatures.concat(signatures);
          beforeSignature = signatures[signatures.length - 1]?.signature;
          
          // Loaded transaction page
          
          // Enhanced rate limiting protection
        if (page < maxPages - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay between pages (increased from 1s)
        }  
          
        } catch (rateLimitError) {
          // Rate limit hit, continuing with available data
          break;
        }
      }
      
      // Found total memo transactions
      
      const znoospaceBeams = [];
      
      // Also check for specific known beam transactions
      const knownBeams = [
        'P6eaC5WMJETHAqsNS1H4U9FcaAyv6ndNV1xCV9Xdb3dwH3aDe1P2Z4uBKKDbXW78KtEhRAAGGeE4H4EVZeirT1G'
      ];
      
      // Check known beams first
      for (const sig of knownBeams) {
        try {
          const targetTx = await this.connection.getTransaction(sig, {
            commitment: 'confirmed'
          });
          
          if (targetTx && targetTx.meta && !targetTx.meta.err) {
            const beamData = this.extractZnoospaceBeamFromTransaction(targetTx, sig);
            if (beamData) {
              // Found known beam transaction
              znoospaceBeams.push(beamData);
            }
          }
        } catch (targetError) {
          // Known beam not found
        }
      }
      
      // Process all signatures for comprehensive timeline
      const batchSize = 5; // Process in smaller batches to avoid rate limits (reduced from 10)
      
      for (let i = 0; i < allSignatures.length; i += batchSize) {
        const batch = allSignatures.slice(i, i + batchSize);
        const progress = Math.min(30 + Math.floor((i / allSignatures.length) * 60), 90);
        
        // Update loading progress if callback is available
        if (this.updateLoadingProgress && typeof this.updateLoadingProgress === 'function') {
          this.updateLoadingProgress('postResult', `Processing beams ${i + 1}-${Math.min(i + batchSize, allSignatures.length)} of ${allSignatures.length}...`, progress);
        }
        
        await Promise.all(batch.map(async (sigInfo) => {
          try {
            const transaction = await this.connection.getTransaction(sigInfo.signature, {
              commitment: 'confirmed',
              maxSupportedTransactionVersion: 0
            });
            
            if (transaction && transaction.meta && !transaction.meta.err) {
              // Check if this transaction involves the memo program
              const memoProgramId = 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr';
              const hasMemoInstruction = transaction.transaction.message.instructions.some(instruction => {
                const programId = transaction.transaction.message.accountKeys[instruction.programIdIndex];
                return programId && programId.toString() === memoProgramId;
              });
              
              if (hasMemoInstruction) {
                // Found memo transaction
                // Look for Znoospace beam data in the transaction
                const beamData = this.extractZnoospaceBeamFromTransaction(transaction, sigInfo.signature);
                if (beamData) {
                  // Successfully extracted beam data
                  // GLOBAL TIMELINE: Include all posts unless filtering by specific wallet
                  if (!walletAddress) {
                    // Global timeline: include ALL posts from ALL users
                    znoospaceBeams.push(beamData);
                  } else if (beamData.author === walletAddress) {
                    // Specific wallet: only include posts from that wallet
                    znoospaceBeams.push(beamData);
                  }
                } else {
                  // Memo transaction found but no beam data extracted
                }
              }
            }
          } catch (txError) {
            // Could not parse transaction
          }
        }));
        
        // Rate limiting between batches
        if (i + batchSize < allSignatures.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Found Znoospace beams
      // Update loading progress if callback is available
      if (this.updateLoadingProgress && typeof this.updateLoadingProgress === 'function') {
        this.updateLoadingProgress('postResult', `Timeline complete! Found ${znoospaceBeams.length} beams`, 100);
      }
      
      // Sort beams by creation time (newest first) - X-like chronological order
      const sortedBeams = znoospaceBeams.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // Remove duplicates based on transaction signature
      const uniqueBeams = sortedBeams.filter((beam, index, self) => 
        index === self.findIndex(b => b.transactionSignature === beam.transactionSignature)
      );
      
      // Final timeline prepared
      
      return uniqueBeams;
      
    } catch (error) {
      console.error('❌ Error querying beams from blockchain:', error);
      return [];
    }
  }

  // Cache management methods
  getCachedBeams(cacheKey) {
    const cached = this.beamCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    // Remove expired cache
    if (cached) {
      this.beamCache.delete(cacheKey);
    }
    return null;
  }

  cacheBeams(cacheKey, data) {
    // Limit cache size
    if (this.beamCache.size >= this.maxCacheSize) {
      // Remove oldest entries
      const entries = Array.from(this.beamCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, Math.floor(this.maxCacheSize / 2));
      toRemove.forEach(([key]) => this.beamCache.delete(key));
    }
    
    this.beamCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  clearBeamCache() {
    this.beamCache.clear();
    // Beam cache cleared
  }

  // Get paginated fallback beams
  getPaginatedFallbackBeams(page, pageSize) {
    // Loading fallback beams page
    
    // Try to load from localStorage first
    try {
      const rawBeams = secureStorage.getItem('znoospace_beams');
      const storedBeams = rawBeams ? (typeof rawBeams === 'string' ? rawBeams : JSON.stringify(rawBeams)) : null;
      if (storedBeams) {
        const beams = JSON.parse(storedBeams);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedBeams = beams.slice(startIndex, endIndex);
        
        return {
          beams: paginatedBeams,
          page,
          pageSize,
          totalBeams: beams.length,
          hasMore: endIndex < beams.length
        };
      }
    } catch (error) {
      // Error loading beams from localStorage
    }
    
    // Use in-memory fallback posts
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedBeams = this.fallbackPosts.slice(startIndex, endIndex);
    
    return {
      beams: paginatedBeams,
      page,
      pageSize,
      totalBeams: this.fallbackPosts.length,
      hasMore: endIndex < this.fallbackPosts.length
    };
  }

  // Load more beams (for infinite scroll)
  async loadMoreBeams(currentPage) {
    const nextPage = currentPage + 1;
    // Loading more beams
    return await this.getBeams(nextPage);
  }

  // Refresh beams (clear cache and reload)
  async refreshBeams() {
    // Refreshing beams
    this.clearBeamCache();
    return await this.getBeams(1);
  }

// Extract Znoospace beam data from a transaction (using admin panel approach)
  extractZnoospaceBeamFromTransaction(transaction, signature) {
    try {
      // Use the admin panel approach: extract from transaction logs, not instruction data
      if (!transaction || !transaction.meta || !transaction.meta.logMessages) {
        return null;
      }

      // Look for memo logs containing Znoospace beam data
      const memoLogs = transaction.meta.logMessages.filter(log => 
        log.includes('Znoospace Beam:') || log.includes('ChirpX Post:')
      );
      
      if (memoLogs.length === 0) {
        return null;
      }
      
      const memoLog = memoLogs[0];
      // Found memo log
      
      // Extract beam data from the log message
      let beamMatch = memoLog.match(/Znoospace Beam: (.+)/);
      if (!beamMatch) {
        // Try legacy ChirpX format for backward compatibility
        beamMatch = memoLog.match(/ChirpX Post: (.+)/);
      }
      
      if (!beamMatch || !beamMatch[1]) {
        // No beam data found in memo log
        return null;
      }
      
      const rawJson = beamMatch[1].trim();
      // Raw JSON from blockchain
      
      try {
        // Clean and parse the JSON data (using admin panel method)
        const cleanJson = this.cleanBlockchainJSON(rawJson);
        const beamData = JSON.parse(cleanJson);
        
        // Check if we have valid beam data structure (like admin panel)
        if (beamData && beamData.content && beamData.author) {
          return {
            id: signature,
            transactionSignature: signature,
            authority: beamData.author,
            author: beamData.author,
            username: beamData.username || `user_${beamData.author.slice(0, 8)}`,
            displayName: beamData.displayName || `User ${beamData.author.slice(0, 8)}`,
            content: beamData.content, // Extract just the content like admin panel
            mediaUrl: beamData.mediaUrl || '',
            timestamp: beamData.timestamp || new Date().toISOString(),
            createdAt: beamData.timestamp || new Date().toISOString(),
            likesCount: 0,
            likes: 0,
            commentsCount: 0,
            explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
          };
        }
        
      } catch (parseError) {
        // Failed to parse JSON
        return null;
      }
      
    } catch (error) {
      // Error extracting beam data
      return null;
    }
  }
  
  // Clean blockchain JSON data (using working admin panel method)
  cleanBlockchainJSON(rawJson) {
    let cleanJson = rawJson.trim();
    cleanJson = cleanJson.replace(/\\\\\"/g, '\"');
    const lastBraceIndex = cleanJson.lastIndexOf('}');
    if (lastBraceIndex !== -1) {
        cleanJson = cleanJson.substring(0, lastBraceIndex + 1);
    }
    return cleanJson;
  }

  // Create post using real Solana transaction with memo
  async createPostTransaction(content, imageUrl = '') {
    // Creating post with real Solana transaction
    
    try {
      if (!this.wallet || !this.wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      // Calculate fees
      const baseFee = 5000; // Base transaction fee in lamports (0.000005 SOL)
      const creatorFee = Math.floor(baseFee * 0.0001); // 0.01% of base fee
      const totalFee = baseFee + creatorFee;

      console.log('Transaction fees estimated', {
        baseFee: baseFee + ' lamports',
        creatorFee: creatorFee + ' lamports', 
        totalFee: totalFee + ' lamports'
      });

      // Get user profile data for username/display name
      let userProfile = null;
      try {
        // Simple fallback - use basic profile data
        userProfile = {
          displayName: 'User',
          username: this.wallet.publicKey.toString().slice(0, 8),
          author: this.wallet.publicKey.toString()
        };
        console.log('✅ Using basic profile for post:', {
          displayName: userProfile.displayName,
          username: userProfile.username
        });
      } catch (error) {
        console.log('⚠️ Could not fetch user profile for post creation:', error.message);
      }

      // Create post data with username/display name
      const postData = {
        content: content,
        mediaUrl: imageUrl || null,
        author: this.wallet.publicKey.toString(),
        username: userProfile ? userProfile.username : `user_${this.wallet.publicKey.toString().slice(0, 8)}`,
        displayName: userProfile ? userProfile.displayName : `User ${this.wallet.publicKey.toString().slice(0, 8)}`,
        timestamp: new Date().toISOString()
      };

      // Create transaction
      const transaction = new Transaction();

      // Add memo instruction with post data
      const memoInstruction = new TransactionInstruction({
        keys: [],
        programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'), // Memo program
        data: Buffer.from(`Znoospace Beam: ${JSON.stringify(postData)}`, 'utf8')
      });
      transaction.add(memoInstruction);

      // Add fee transfer to contract creator (if fee > 0)
      if (creatorFee > 0) {
        const feeTransfer = SystemProgram.transfer({
          fromPubkey: this.wallet.publicKey,
          toPubkey: CONTRACT_CREATOR_WALLET,
          lamports: creatorFee
        });
        transaction.add(feeTransfer);
      }

      // Get recent blockhash
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      // Signing and sending transaction
      
      // Sign and send transaction
      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');
      
      // Transaction confirmed

      // Create post object for local storage with username/display name
      const localPostData = {
        id: signature, // Use transaction signature as post ID
        authority: this.wallet.publicKey.toString(),
        username: postData.username,
        displayName: postData.displayName,
        content,
        imageUrl,
        likesCount: 0,
        repostsCount: 0,
        createdAt: Date.now(),
        transactionSignature: signature
      };
      
      // Store the post locally for immediate display
      this.fallbackPosts.unshift(localPostData);
      
      return {
        success: true,
        signature: signature,
        post: localPostData,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
        fees: {
          baseFee,
          creatorFee,
          totalFee
        }
      };
      
    } catch (error) {
      console.error('Transaction post creation failed:', error);
      throw new Error(`Transaction post creation failed: ${error.message}`);
    }
  }

  // Create NFT minting transaction with ZNOOZINU payment
  async createNFTMintTransaction(tierData, avatarTraits, testMode = true) {
    try {
      if (!this.wallet || !this.wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      console.log('🌌 Starting NFT minting transaction:', {
        tier: tierData.name,
        price: tierData.price,
        traits: avatarTraits,
        testMode: testMode
      });

      // Import required modules
      const { Transaction, TransactionInstruction, SystemProgram, PublicKey } = await import('@solana/web3.js');
      const { TOKEN_PROGRAM_ID, createTransferInstruction, getAssociatedTokenAddress } = await import('@solana/spl-token');

      // ZNOOZINU token mint address (hardcoded for security)
      const ZNOOZINU_MINT = new PublicKey('5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN');
      // Use the existing contract creator wallet from the top of the file

      // Calculate fees
      const baseFee = 10000; // Base transaction fee in lamports (0.00001 SOL)
      const creatorFee = Math.floor(baseFee * 0.001); // 0.1% of base fee
      const totalSOLFee = baseFee + creatorFee;
      const znoozinuAmount = tierData.price * 1000000000; // Convert to smallest unit (9 decimals)

      console.log('💰 Transaction costs calculated:', {
        solFee: totalSOLFee + ' lamports',
        znoozinuCost: tierData.price + ' ZNOOZINU',
        znoozinuAmount: znoozinuAmount + ' (smallest unit)'
      });

      // Get user's ZNOOZINU token account
      const userTokenAccount = await getAssociatedTokenAddress(
        ZNOOZINU_MINT,
        this.wallet.publicKey
      );

      // Get contract creator's ZNOOZINU token account
      const creatorTokenAccount = await getAssociatedTokenAddress(
        ZNOOZINU_MINT,
        CONTRACT_CREATOR_WALLET
      );

      // Create NFT metadata
      const nftMetadata = {
        name: `${tierData.name} Cosmic Avatar #${Date.now()}`,
        symbol: 'COSMIC',
        description: tierData.description,
        image: '', // TODO: Generate/upload actual avatar image
        attributes: Object.entries(avatarTraits).map(([trait_type, value]) => ({
          trait_type: trait_type.charAt(0).toUpperCase() + trait_type.slice(1),
          value
        })),
        properties: {
          category: 'image',
          creators: [{
            address: CONTRACT_CREATOR_WALLET.toString(),
            share: 100
          }]
        },
        tier: tierData.name,
        rarity: tierData.rarity,
        mintedAt: new Date().toISOString(),
        mintedBy: this.wallet.publicKey.toString()
      };

      // Create transaction
      const transaction = new Transaction();

      // Add ZNOOZINU token transfer instruction (payment) - only if not in test mode
      if (!testMode) {
        const tokenTransferInstruction = createTransferInstruction(
          userTokenAccount,
          creatorTokenAccount,
          this.wallet.publicKey,
          znoozinuAmount,
          [],
          TOKEN_PROGRAM_ID
        );
        transaction.add(tokenTransferInstruction);
        console.log('💰 Added ZNOOZINU payment instruction');
      } else {
        console.log('🧪 Test mode: Skipping ZNOOZINU payment');
      }

      // Add memo instruction with NFT metadata
      const memoInstruction = new TransactionInstruction({
        keys: [],
        programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'), // Memo program
        data: Buffer.from(`Znoospace NFT Mint: ${JSON.stringify(nftMetadata)}`, 'utf8')
      });
      transaction.add(memoInstruction);

      // Add SOL fee transfer to contract creator
      if (creatorFee > 0) {
        const feeTransfer = SystemProgram.transfer({
          fromPubkey: this.wallet.publicKey,
          toPubkey: CONTRACT_CREATOR_WALLET,
          lamports: creatorFee
        });
        transaction.add(feeTransfer);
      }

      // Get recent blockhash
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      console.log('📝 Transaction prepared, requesting signature...');

      // Sign and send transaction
      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      console.log('🚀 Transaction sent, waiting for confirmation...', signature);

      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');
      
      console.log('✅ NFT minting transaction confirmed!');

      // Create NFT object for local storage
      const mintedNFT = {
        id: signature.slice(0, 8), // Use first 8 chars of signature as ID
        mintAddress: signature, // Use signature as mint address for now
        tier: tierData.name,
        cost: tierData.price,
        traits: avatarTraits,
        metadata: nftMetadata,
        transactionSignature: signature,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
        mintedAt: new Date().toISOString(),
        owner: this.wallet.publicKey.toString()
      };

      return {
        success: true,
        nft: mintedNFT,
        signature: signature,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
        fees: {
          solFee: totalSOLFee,
          znoozinuCost: tierData.price
        }
      };
      
    } catch (error) {
      console.error('❌ NFT minting transaction failed:', error);
      throw new Error(`NFT minting failed: ${error.message}`);
    }
  }
}

// React hook to use SolanaService
export const useSolanaService = (wallet) => {
  const { connection } = useConnection();
  const walletAdapter = useWallet();

  const solanaService = new SolanaService(connection, wallet || walletAdapter);

  return {
    solanaService,
    isConnected: (wallet || walletAdapter).connected,
    publicKey: (wallet || walletAdapter).publicKey,
    wallet: wallet || walletAdapter
  };
};
